import { FormArray, FormBuilder, Validators } from "react-reactive-form";

/**
 * Calculate initial Comparative PK block IDs from saved data.
 * Returns a sorted array of block IDs.
 */
export const calculateInitialCompBlockIds = wizardForm => {
  // Detect from actual controls first
  const analytesControls = Object.keys(wizardForm.controls).filter(key =>
    /^qPkAnalytes(_\d+)?$/.test(key)
  );
  if (analytesControls.length > 0) {
    const ids = analytesControls.map(key => {
      const match = key.match(/^qPkAnalytes_(\d+)$/);
      return match ? parseInt(match[1]) : 1;
    });
    return [...new Set(ids)].sort((a, b) => a - b);
  }
  // Fallback: tracking array
  const formValues = wizardForm.value;
  if (formValues.qPkCompAnalysis && Array.isArray(formValues.qPkCompAnalysis)) {
    const len = Math.max(1, formValues.qPkCompAnalysis.length);
    return Array.from({ length: len }, (_, i) => i + 1);
  }
  return [1];
};

/**
 * Check if a Comparative PK block should use prefixed controls
 */
export const shouldUsePrefixedControls = (
  isFirstBlock,
  stdEnabled,
  stdBlocksCount,
  blockNumber
) => {
  const stdHasBlocks = stdEnabled && stdBlocksCount > 0;
  const stdBlockWithSameNumberExists =
    stdHasBlocks && stdBlocksCount >= blockNumber;
  return (
    (isFirstBlock && stdHasBlocks) ||
    (!isFirstBlock && stdBlockWithSameNumberExists)
  );
};

/**
 * Add a new Comparative PK block with the given block ID.
 */
export const addCompPkBlock = (wizardForm, blockId) => {
  const suffix = blockId > 1 ? `_${blockId}` : "";

  // Always use "Comp" prefix for shared fields to guarantee isolation from Standard PK.
  const sharedFieldPrefix = "Comp";

  const controlsToEnsure = [
    {
      name: `qPkTable${sharedFieldPrefix}${suffix}`,
      factory: () => FormBuilder.control("", Validators.required),
    },
    {
      name: `qPkPopulation${sharedFieldPrefix}${suffix}`,
      factory: () => FormBuilder.control(""),
    },
    {
      name: `qPkMultiDose${sharedFieldPrefix}${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
    {
      name: `qPkFigures${sharedFieldPrefix}${suffix}`,
      factory: () => FormBuilder.control([], Validators.required),
    },
    {
      name: `qPkFiguresExtra${sharedFieldPrefix}${suffix}`,
      factory: () => FormBuilder.control([], Validators.required),
    },
    {
      name: `qPkTimelines${sharedFieldPrefix}${suffix}`,
      factory: () => new FormArray([]),
    },
    {
      name: `qPkStatsAnalysis${sharedFieldPrefix}${suffix}`,
      factory: () => FormBuilder.control([]),
    },
    {
      name: `qPkComparisonName${suffix}`,
      factory: () => FormBuilder.control(""),
    },
    {
      name: `qPkComparisonPairs${suffix}`,
      factory: () => new FormArray([]),
    },
    { name: `qPkAnalytes${suffix}`, factory: () => new FormArray([]) },
    { name: `qPkExposure${suffix}`, factory: () => new FormArray([]) },
    {
      name: `qPkDoseInteraction${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
  ];

  controlsToEnsure.forEach(({ name, factory }) => {
    if (!wizardForm.contains(name)) {
      wizardForm.addControl(name, factory());
    } else if (
      wizardForm.get(name) instanceof FormArray &&
      wizardForm.get(name).length > 0
    ) {
      while (wizardForm.get(name).length > 0) {
        wizardForm.get(name).removeAt(0);
      }
    }
  });
};

/**
 * Remove a Comparative PK block by its block ID.
 * Simply removes the block's controls — no data shifting needed.
 */
export const removeCompPkBlock = (wizardForm, blockId) => {
  const suffix = blockId > 1 ? `_${blockId}` : "";
  const sharedFieldPrefix = "Comp";

  const controlsToRemove = [
    `qPkTable${sharedFieldPrefix}${suffix}`,
    `qPkPopulation${sharedFieldPrefix}${suffix}`,
    `qPkMultiDose${sharedFieldPrefix}${suffix}`,
    `qPkFigures${sharedFieldPrefix}${suffix}`,
    `qPkFiguresExtra${sharedFieldPrefix}${suffix}`,
    `qPkTimelines${sharedFieldPrefix}${suffix}`,
    `qPkComparisonName${suffix}`,
    `qPkComparisonPairs${suffix}`,
    `qPkAnalytes${suffix}`,
    `qPkExposure${suffix}`,
    `qPkDoseInteraction${suffix}`,
    `qPkStatsAnalysis${sharedFieldPrefix}${suffix}`,
  ];

  controlsToRemove.forEach(name => {
    if (wizardForm.contains(name)) {
      wizardForm.removeControl(name);
    }
  });
};

/**
 * Duplicate an existing Comparative PK block into a new block ID.
 */
export const duplicateCompPkBlock = (wizardForm, sourceBlockId, newBlockId) => {
  addCompPkBlock(wizardForm, newBlockId);

  const sourceSuffix = sourceBlockId > 1 ? `_${sourceBlockId}` : "";
  const newSuffix = newBlockId > 1 ? `_${newBlockId}` : "";
  const sharedFieldPrefix = "Comp";

  const scalarControls = [
    `qPkTable${sharedFieldPrefix}`,
    `qPkPopulation${sharedFieldPrefix}`,
    `qPkMultiDose${sharedFieldPrefix}`,
    `qPkFigures${sharedFieldPrefix}`,
    `qPkFiguresExtra${sharedFieldPrefix}`,
    `qPkStatsAnalysis${sharedFieldPrefix}`,
    "qPkComparisonName",
    "qPkDoseInteraction",
  ];

  scalarControls.forEach(baseName => {
    const sourceControl = wizardForm.get(`${baseName}${sourceSuffix}`);
    const newControl = wizardForm.get(`${baseName}${newSuffix}`);
    if (!sourceControl || !newControl) return;
    const value = sourceControl.value;
    newControl.setValue(Array.isArray(value) ? [...value] : value);
  });

  const formArrayControls = [
    "qPkTimelinesComp",
    "qPkComparisonPairs",
    "qPkAnalytes",
    "qPkExposure",
  ];

  formArrayControls.forEach(baseName => {
    const sourceArray = wizardForm.get(`${baseName}${sourceSuffix}`);
    const newArray = wizardForm.get(`${baseName}${newSuffix}`);
    if (!(sourceArray instanceof FormArray)) return;
    if (!(newArray instanceof FormArray)) return;
    while (newArray.length > 0) newArray.removeAt(0);
    sourceArray.controls.forEach((sourceGroup, index) => {
      const sourceValue = sourceGroup.value || {};
      const groupConfig = Object.keys(sourceValue).reduce((acc, key) => {
        acc[key] = [sourceValue[key] ?? ""];
        return acc;
      }, {});
      const newGroup = FormBuilder.group(groupConfig);
      newGroup.meta = { key: Date.now() + index };
      newArray.push(newGroup);
    });
  });
};

/**
 * Helper function to get analytes from question 19g as dropdown options
 */
export const updatePkAnalytesComparisonAdditionalData = (
  wizardForm,
  suffix = ""
) => {
  const controlName = `qPkAnalytes${suffix}`;
  let qPkAnalytesComparisonAdditionalData = {
    Analyte: [],
    Comparator: [],
  };

  if (!wizardForm.contains(controlName)) {
    return qPkAnalytesComparisonAdditionalData;
  }

  const control = wizardForm.controls[controlName];
  if (!control || !control.value) {
    return qPkAnalytesComparisonAdditionalData;
  }

  const list = control.value
    .map(v => v["valueHelper"])
    .filter(v => v !== "")
    .map(v => ({ title: v, value: v }));

  qPkAnalytesComparisonAdditionalData = {
    Analyte: list,
    Comparator: list,
  };

  return qPkAnalytesComparisonAdditionalData;
};
